/**
 * Catches render-time exceptions anywhere below it in the tree. React error
 * boundaries must be class components -- there is no hook equivalent.
 * Distinct from `BundleErrorState`, which renders a real, already-known
 * *data* failure (a bundle that failed to load or validate); this catches
 * unexpected exceptions in rendering code itself.
 */
import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | undefined;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: undefined };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("ExploitLens: unhandled render error", error, info.componentStack);
  }

  private reset = (): void => {
    this.setState({ error: undefined });
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div role="alert" className="mx-auto max-w-xl px-6 py-16 text-center">
        <p className="font-editorial text-xl text-ink">
          Something failed while rendering this view.
        </p>
        <p className="mt-3 text-sm text-ink-muted">{error.message}</p>
        <button
          type="button"
          onClick={this.reset}
          className="mt-6 rounded border border-rule-strong px-4 py-2 text-sm text-ink hover:bg-surface"
        >
          Try again
        </button>
      </div>
    );
  }
}
